<div data-testid="offer-list-page" class="offer-list-container flex-table-page">
  <app-confirmation-dialog
    data-testid="offer-list-status-change-confirmation-dialog"
    [(visible)]="showStatusChangeDialog"
    [title]="'promotion_management.status_change.title' | translate"
    [message]="getStatusChangeMessage()"
    [buttons]="statusChangeButtons"
    (emitOnClick)="onStatusChangeConfirmation($event)"
  />

  <p-toast data-testid="offer-list-toast" styleClass="toast-above-modal"></p-toast>

  <h2 class="text-2xl font-semibold text-gray-800 mb-3 flex-table-header">
    {{ "offer.management.title" | translate }}
  </h2>

  <app-search-form
    data-testid="offer-list-search-form"
    [searchFormat]="searchFields()"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-3 items-center">
      <app-button
        [testId]="'offer-list-download-template-btn'"
        *ngIf="canDownloadTemplate()"
        type="secondary"
        [outlined]="true"
        icon="download"
        [label]="'offer.toolbar.download_template' | translate"
        (emitOnClick)="onDownloadTemplate()"
      ></app-button>

      <app-button
        [testId]="'offer-list-import-offers-btn'"
        *ngIf="canCreate()"
        type="info"
        [outlined]="false"
        icon="add"
        [label]="'offer.toolbar.import_offers' | translate"
        (emitOnClick)="onImportOffers()"
      ></app-button>

      <!-- Column config button -->
      <app-button
        [testId]="'offer-list-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>

      <!-- Status filter primary action kept as New Offer shortcut -->
      <app-status-filter
        [primaryButton]="primaryButton()"
        (primaryButtonClick)="onAddOffer()"
      ></app-status-filter>
    </div>
  </app-search-form>

  <div class="flex-table-section">
    <advanced-data-table
      data-testid="offer-list-table"
      #advancedTable
      [dataSource]="offers()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [rows]="searchParams().size || DEFAULT_PAGE_SIZE"
      [first]="
        (searchParams().page || 0) * (searchParams().size || DEFAULT_PAGE_SIZE)
      "
      [totalRecords]="totalItems()"
      [hasCheckColumn]="false"
      [showInternalColumnConfigButton]="false"
      scrollHeight="flex"
      (pageChange)="onPageChange($event)"
      (sortChange)="onSort($event)"
      class="w-full"
    >
    <ng-template
      #customTemplate
      let-value
      let-rowData="rowData"
      let-column="column"
    >
      @if (column && column.template === "kindTemplate") {
        <p-tag
          [attr.data-testid]="'offer-list-kind-tag-' + rowData.id"
          [value]="getKindDisplay(value)"
          [severity]="getKindSeverity(value)"
          styleClass="type-tag"
          [ngStyle]="{ 'max-width': '260px' }"
        />
      } @else if (column && column.template === "validityTemplate") {
        <span class="text-xs text-gray-600 whitespace-pre-line">
          {{ getValidityDisplay(rowData) }}
        </span>
      } @else if (column && column.template === "datetimeTemplate") {
        <span class="text-xs text-gray-600">
          {{ value ? formatDateTime(value) : "-" }}
        </span>
      }
    </ng-template>

    <ng-template #statusTemplate let-status let-rowData="rowData">
      <div class="flex items-center justify-center gap-1 w-full">
        <p-tag
          [attr.data-testid]="'offer-list-status-tag-' + rowData.id"
          [value]="getStatusLabel(rowData.status) | translate"
          [severity]="getStatusSeverity(rowData.status)"
          styleClass="status-tag"
          [ngStyle]="getStatusStyle(rowData.status)"
        />
      </div>
    </ng-template>

    <ng-template #actionsTemplate let-rowData>
      <div class="flex gap-2 justify-center flex-wrap">
        @if (canUpdate()) {
          <app-button
            [testId]="'offer-list-action-edit-' + rowData.id"
            class="action-edit"
            [label]="'common.actions.edit' | translate"
            type="action"
            (emitOnClick)="onEdit(rowData)"
          ></app-button>
        }

        @if (canApprove()) {
          <app-button
            [testId]="
              'offer-list-action-' +
              (rowData.status === 'active' ? 'deactivate' : 'activate') +
              '-' +
              rowData.id
            "
            [class]="
              rowData.status === 'active' ? 'action-disable' : 'action-enable'
            "
            [label]="
              rowData.status === 'active'
                ? ('offer.actions.deactivate' | translate)
                : ('offer.actions.activate' | translate)
            "
            type="action"
            (emitOnClick)="onToggleStatus(rowData)"
          ></app-button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>

  <!-- Template Selection Dialog -->
  <p-dialog
    data-testid="offer-list-template-dialog"
    [visible]="showTemplateDialog()"
    [modal]="true"
    [closable]="false"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '90vw', maxWidth: '800px' }"
    [styleClass]="'promotion-add-modal'"
    (onHide)="onCloseTemplateDialog()"
  >
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-semibold text-gray-800 m-0">
          {{ "offer.template.select_title" | translate }}
        </h2>
        <button
          data-testid="offer-list-template-dialog-close-btn"
          type="button"
          class="custom-close-btn"
          (click)="onCloseTemplateDialog()"
          [attr.aria-label]="'common.actions.close_dialog' | translate"
        >
          <svg
            class="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </ng-template>

    <div class="template-selection-content pt-4">
      <p class="text-gray-600 mb-6">
        {{ "offer.template.select_description" | translate }}
      </p>

      <div class="template-files-list">
        @if (templateLoading()) {
          <div class="text-center py-8">
            <div class="inline-flex items-center justify-center">
              <div
                data-testid="offer-list-template-loading"
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
              ></div>
              <span class="ml-3 text-gray-600">
                {{ "common.messages.loading" | translate }}
              </span>
            </div>
          </div>
        } @else if (templates().length === 0) {
          <div class="text-center py-8">
            <span
              class="material-symbols-outlined text-4xl text-gray-400 mb-4 block"
              >description</span
            >
            <p class="text-gray-500">
              {{ "offer.template.no_files" | translate }}
            </p>
          </div>
        } @else {
          @for (
            t of templates();
            track t.url || t.name;
            let templateIndex = $index
          ) {
            <button
              [attr.data-testid]="'offer-list-template-file-' + templateIndex"
              type="button"
              class="template-file-card"
              [class.selected]="selectedTemplateFile()?.url === t.url"
              (click)="selectTemplateFile(t)"
            >
              <div class="template-file-icon">
                <span class="material-symbols-outlined text-2xl"
                  >description</span
                >
              </div>
              <div class="template-file-info">
                <h3
                  class="template-file-name"
                  [pTooltip]="t.name"
                  tooltipPosition="top"
                  [showDelay]="300"
                >
                  {{ t.name }}
                </h3>
              </div>

              @if (selectedTemplateFile()?.url === t.url) {
                <div class="template-file-selected">
                  <svg
                    class="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </div>
              }
            </button>
          }
        }
      </div>

      <div class="flex justify-end space-x-3 pt-6">
        <app-button
          [testId]="'offer-list-template-cancel-btn'"
          [label]="'common.actions.cancel' | translate"
          type="secondary"
          [outlined]="true"
          (emitOnClick)="onCloseTemplateDialog()"
        ></app-button>
        <app-button
          [testId]="'offer-list-template-download-btn'"
          [label]="'common.actions.download' | translate"
          type="info"
          [disabled]="!selectedTemplateFile() || templateLoading()"
          (emitOnClick)="downloadSelectedTemplateFile()"
        ></app-button>
      </div>
    </div>
  </p-dialog>

  <p-dialog
    data-testid="offer-list-import-dialog"
    [visible]="showCreateDialog()"
    [modal]="true"
    [closable]="false"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '90vw', maxWidth: '800px' }"
    [styleClass]="'promotion-add-modal'"
    (onHide)="onCloseCreateDialog()"
  >
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-semibold text-gray-800 m-0">
          {{ "offer.import.title" | translate }}
        </h2>
        <button
          data-testid="offer-list-import-dialog-close-btn"
          type="button"
          class="custom-close-btn"
          (click)="onCloseCreateDialog()"
          [attr.aria-label]="'common.actions.close_dialog' | translate"
        >
          <svg
            class="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </ng-template>

    <div class="promotion-add-container modal-mode">
      <div class="max-w-4xl mx-auto p-0 pt-4">
        <p-card>
          <ng-template pTemplate="header">
            <div class="p-4 border-b border-gray-200">
              <h2 class="text-lg font-semibold text-gray-900">
                {{ "offer.import.rule_file_title" | translate }}
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                {{ "offer.import.rule_file_description" | translate }}
              </p>
            </div>
          </ng-template>

          <div class="space-y-4">
            <app-file-upload
              data-testid="offer-list-import-file-upload"
              #importFileUpload
              [placeholder]="'offer.import.file_upload_placeholder' | translate"
              accept=".xlsx,.xls"
              [acceptedTypes]="'.xlsx,.xls'"
              (fileChange)="onCreateFileChanged($event)"
            ></app-file-upload>
          </div>
        </p-card>
      </div>
    </div>
    <ng-template pTemplate="footer">
      <div class="flex items-center justify-end gap-3">
        <app-button
          [testId]="'offer-list-import-cancel-btn'"
          [label]="'common.actions.cancel' | translate"
          (emitOnClick)="onCloseCreateDialog()"
          [outlined]="true"
          type="secondary"
        ></app-button>
        <app-button
          [testId]="'offer-list-import-create-btn'"
          [label]="'offer.import.create_button' | translate"
          type="info"
          [disabled]="createLoading() || !canSubmitImport()"
          [loading]="createLoading()"
          (emitOnClick)="onConfirmCreateFromFile()"
        ></app-button>
      </div>
    </ng-template>
  </p-dialog>
</div>
